import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";

export const THEME_STORAGE_KEY = "theme";
export const DARK_THEME = "dark";
export const LIGHT_THEME = "light";

const ThemeContext = createContext();

const getInitialTheme = () => {
    if (typeof window === "undefined") {
        return DARK_THEME;
    }
    return window.localStorage.getItem(THEME_STORAGE_KEY) === LIGHT_THEME
        ? LIGHT_THEME
        : DARK_THEME;
};

const applyTheme = (theme) => {
    document.documentElement.setAttribute("data-theme", theme);
};

export const ThemeProvider = ({ children }) => {
    const [theme, setThemeState] = useState(getInitialTheme);

    useEffect(() => {
        applyTheme(theme);
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }, [theme]);

    const setTheme = useCallback((nextTheme) => {
        setThemeState(nextTheme === LIGHT_THEME ? LIGHT_THEME : DARK_THEME);
    }, []);

    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: theme,
            primary: {
                main: "rgb(40, 102, 223)",
            },
            background: {
                default: theme === DARK_THEME ? "#171717" : "#f4f6f9",
                paper: theme === DARK_THEME ? "#242424" : "#ffffff",
            },
            text: {
                primary: theme === DARK_THEME ? "#ffffff" : "#1a1d21",
                secondary: theme === DARK_THEME ? "#d0d0d0" : "#4a5060",
            },
        },
        typography: {
            fontFamily: '"Inter", sans-serif',
        },
    }), [theme]);

    const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={muiTheme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export const useThemeMode = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useThemeMode must be used within a ThemeProvider");
    }
    return context;
};
